import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Scale, CheckSquare, AlertCircle, Calendar, Plus, Trash2, ArrowRight, ChevronLeft, ChevronRight, X, CheckCircle } from 'lucide-react';
import { EmptyState } from '../components/EmptyState';
import { InfoTooltip } from '../components/InfoTooltip';
import { Tool, AppSettings, GovernanceTask, DEFAULT_CRITICALITY_LEVELS } from '../types';
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const PRIORITY_ORDER: Record<string, number> = { HIGH: 0, MEDIUM: 1, LOW: 2 };
const PRIORITY_BADGE: Record<string, { bg: string; text: string }> = {
  HIGH: { bg: 'bg-red-100', text: 'text-red-700' },
  MEDIUM: { bg: 'bg-orange-100', text: 'text-orange-700' },
  LOW: { bg: 'bg-emerald-100', text: 'text-emerald-700' },
};

interface GovernanceViewProps {
  tools: Tool[];
  settings: AppSettings;
  governanceTasks: GovernanceTask[];
  onUpdateTool?: (tool: Tool) => Promise<void>;
  onAddTask: (task: GovernanceTask) => Promise<GovernanceTask[]>;
  onToggleTask: (id: string) => Promise<GovernanceTask[]>;
  onDeleteTask: (id: string) => Promise<GovernanceTask[]>;
  onReloadTasks: () => Promise<GovernanceTask[]>;
}

export const GovernanceView: React.FC<GovernanceViewProps> = ({ tools, settings, governanceTasks, onUpdateTool, onAddTask, onToggleTask, onDeleteTask, onReloadTasks }) => {
  const tasks = governanceTasks;
  const [showAddTask, setShowAddTask] = useState(false);
  
  // Après complétion d'une tâche : proposer rappel (renewal/notice) ou créer une autre
  const [taskJustCompleted, setTaskJustCompleted] = useState<GovernanceTask | null>(null);
  const [nextReminderDate, setNextReminderDate] = useState('');
  
  // Calendrier : mois affiché
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() };
  });
  
  // Formulaire
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskTool, setNewTaskTool] = useState('');
  const [newTaskDate, setNewTaskDate] = useState('');
  const newTaskTitleRef = useRef<HTMLInputElement | null>(null);
  const [complianceShowMore, setComplianceShowMore] = useState(0);
  const [focusedTaskId, setFocusedTaskId] = useState<string | null>(null);

  const handleAddTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTaskTitle) return;
    const newTask: GovernanceTask = {
      id: '', title: newTaskTitle, toolId: newTaskTool,
      assigneeId: '', dueDate: newTaskDate,
      status: 'PENDING', priority: 'MEDIUM', isCompleted: false,
    };
    await onAddTask(newTask);
    setShowAddTask(false);
    setNewTaskTitle('');
    setNewTaskDate('');
  };

  const handleToggleTask = async (id: string) => {
    const list = await onToggleTask(id);
    const toggled = list.find(t => t.id === id);
    if (toggled?.isCompleted) {
      setTaskJustCompleted(toggled);
      if (toggled.title.startsWith('Renewal: ') || toggled.title.startsWith('Notice deadline: ')) {
        const from = toggled.dueDate ? new Date(toggled.dueDate) : new Date();
        from.setFullYear(from.getFullYear() + 1);
        setNextReminderDate(from.toISOString().slice(0, 10));
      } else if (toggled.title.startsWith('Review: ') && toggled.toolId) {
        const tool = tools.find(t => t.id === toggled.toolId);
        const levels = settings.criticalityLevels ?? DEFAULT_CRITICALITY_LEVELS;
        const level = tool ? levels.find(l => l.id === tool.importance) : undefined;
        const months = level?.reviewFrequencyMonths ?? 6;
        const next = new Date();
        next.setMonth(next.getMonth() + months);
        setNextReminderDate(next.toISOString().slice(0, 10));
      }
    }
  };

  const handleDeleteTask = async (id: string) => {
    if (window.confirm("Delete this task?")) {
      await onDeleteTask(id);
    }
  };

  const isToolDateTask = (task: GovernanceTask) =>
    task.title.startsWith('Renewal: ') || task.title.startsWith('Notice deadline: ');

  const isReviewTask = (task: GovernanceTask) =>
    task.title.startsWith('Review: ');

  const handleSetNextReminder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!taskJustCompleted?.toolId || !nextReminderDate || !onUpdateTool) return;
    const tool = tools.find(t => t.id === taskJustCompleted.toolId);
    if (!tool) return;
    const isRenewal = taskJustCompleted.title.startsWith('Renewal: ');
    const updatedTool = {
      ...tool,
      ...(isRenewal ? { renewalDate: nextReminderDate } : { noticeDeadline: nextReminderDate })
    };
    await onUpdateTool(updatedTool);
    await onReloadTasks();
    setTaskJustCompleted(null);
    setNextReminderDate('');
  };

  const openAddTaskAndClose = () => {
    setShowAddTask(true);
    setTaskJustCompleted(null);
  };

  const safeTasks = Array.isArray(tasks) ? tasks : [];
  const activeTasks = useMemo(() => safeTasks.filter((task) => !task.isCompleted), [safeTasks]);

  // Compliance & Tasks : overdue ou à venir dans les 30 prochains jours, puis "Show more" par 10
  const todayStr = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const limit30Str = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().slice(0, 10);
  }, []);

  const { complianceRelevant, complianceRest } = useMemo(() => {
    const relevant: GovernanceTask[] = [];
    const rest: GovernanceTask[] = [];
    for (const t of activeTasks) {
      if (!t.dueDate) {
        rest.push(t);
        continue;
      }
      if (t.dueDate < todayStr) relevant.push(t);
      else if (t.dueDate <= limit30Str) relevant.push(t);
      else rest.push(t);
    }
    const byPriorityThenDate = (a: GovernanceTask, b: GovernanceTask) => {
      const pa = PRIORITY_ORDER[a.priority ?? 'MEDIUM'] ?? 1;
      const pb = PRIORITY_ORDER[b.priority ?? 'MEDIUM'] ?? 1;
      if (pa !== pb) return pa - pb;
      return (a.dueDate || '').localeCompare(b.dueDate || '');
    };
    relevant.sort(byPriorityThenDate);
    rest.sort(byPriorityThenDate);
    return { complianceRelevant: relevant, complianceRest: rest };
  }, [activeTasks, todayStr, limit30Str]);

  const complianceVisible = useMemo(() => {
    const extra = complianceRest.slice(0, complianceShowMore);
    return [...complianceRelevant, ...extra];
  }, [complianceRelevant, complianceRest, complianceShowMore]);

  useEffect(() => {
    if (!focusedTaskId) return;
    const timer = window.setTimeout(() => setFocusedTaskId(null), 2000);
    return () => window.clearTimeout(timer);
  }, [focusedTaskId]);

  // Calendrier : grille du mois
  const { daysInGrid, monthLabel } = useMemo(() => {
    const { year, month } = calendarMonth;
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);
    const startWeekday = first.getDay();
    const monFirst = startWeekday === 0 ? 6 : startWeekday - 1;
    const daysInMonth = last.getDate();
    const totalCells = Math.ceil((monFirst + daysInMonth) / 7) * 7;
    const days: { date: number | null; dateStr: string; isCurrentMonth: boolean }[] = [];
    for (let i = 0; i < totalCells; i++) {
      if (i < monFirst) {
        const prev = new Date(year, month, 1 - (monFirst - i));
        days.push({ date: null, dateStr: prev.toISOString().slice(0, 10), isCurrentMonth: false });
      } else if (i < monFirst + daysInMonth) {
        const d = i - monFirst + 1;
        days.push({
          date: d,
          dateStr: `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`,
          isCurrentMonth: true,
        });
      } else {
        const next = new Date(year, month + 1, i - monFirst - daysInMonth + 1);
        days.push({ date: null, dateStr: next.toISOString().slice(0, 10), isCurrentMonth: false });
      }
    }
    const monthLabel = first.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    return { daysInGrid: days, monthLabel };
  }, [calendarMonth]);

  const getItemsForDate = (dateStr: string) => {
    const taskItems = activeTasks.filter(t => t.dueDate === dateStr);
    const renewalItems = tools.filter(t => t.renewalDate === dateStr);
    return { taskItems, renewalItems };
  };

  const prevMonth = () => {
    setCalendarMonth(prev => (prev.month === 0 ? { year: prev.year - 1, month: 11 } : { year: prev.year, month: prev.month - 1 }));
  };
  const nextMonth = () => {
    setCalendarMonth(prev => (prev.month === 11 ? { year: prev.year + 1, month: 0 } : { year: prev.year, month: prev.month + 1 }));
  };

  // Calcul des renouvellements à venir (basé sur les Outils)
  const today = new Date();
  const upcomingRenewals = tools.filter(tool => {
    if (!tool.renewalDate) return false;
    const renewal = new Date(tool.renewalDate);
    const diffTime = renewal.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 && diffDays <= settings.renewalWarningDays;
  });

  const jumpToRenewalTask = (tool: Tool) => {
    const renewalTask = activeTasks.find(
      (task) =>
        task.toolId === tool.id &&
        task.title.startsWith('Renewal:')
    );

    if (renewalTask) {
      setFocusedTaskId(renewalTask.id);
      window.requestAnimationFrame(() => {
        const el = document.getElementById(`governance-task-${renewalTask.id}`);
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      return;
    }

    // Fallback: open pre-filled task form if no generated renewal task exists yet.
    setShowAddTask(true);
    setNewTaskTitle(`Renewal: ${tool.name}`);
    setNewTaskTool(tool.id);
    setNewTaskDate(tool.renewalDate || '');
  };

  const openAddTaskForDate = (dateStr: string) => {
    setShowAddTask(true);
    setTaskJustCompleted(null);
    setNewTaskDate(dateStr);
    window.requestAnimationFrame(() => {
      const form = document.getElementById('governance-add-task-form');
      form?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      newTaskTitleRef.current?.focus();
    });
  };

  return (
    <div className="space-y-6 animate-slide-in">
      {/* Header */}
      <div className="flex justify-between items-end">
        <div>
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <Scale className="text-indigo-600" />
            Governance & Compliance
          </h2>
          <p className="text-sm text-slate-500 mt-1">Manage renewals, contracts, and compliance tasks.</p>
        </div>
        <button 
          onClick={() => setShowAddTask(!showAddTask)}
          className="bg-indigo-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-indigo-700 transition-colors"
        >
          <Plus size={18} /> Add Task
        </button>
      </div>

      {/* Alerter Renouvellements (Automatique basé sur les Tools) */}
      {upcomingRenewals.length > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
          <h3 className="text-orange-800 font-bold flex items-center gap-2 mb-3">
            <AlertCircle size={20} />
            Upcoming Renewals ({upcomingRenewals.length})
          </h3>
          <div className="grid gap-3 md:grid-cols-2">
            {upcomingRenewals.map(tool => (
              <button
                key={tool.id}
                type="button"
                onClick={() => jumpToRenewalTask(tool)}
                className="w-full text-left bg-white p-3 rounded-lg border border-orange-100 shadow-sm flex justify-between items-center hover:border-orange-300 hover:bg-orange-50 transition-colors"
              >
                <div>
                  <div className="font-bold text-slate-800">{tool.name}</div>
                  <div className="text-xs text-orange-600 font-medium">
                    Renews on {tool.renewalDate}
                  </div>
                </div>
                <ArrowRight size={16} className="text-orange-400" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Formulaire d'ajout */}
      {showAddTask && (
        <div id="governance-add-task-form" className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
            <h3 className="font-bold text-slate-700 mb-4">New Governance Task</h3>
            <form onSubmit={handleAddTask} className="flex flex-col md:flex-row gap-4 items-end">
                <div className="flex-1 w-full">
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Task Title</label>
                    <input 
                        ref={newTaskTitleRef}
                        type="text" 
                        required
                        className="w-full p-2 border border-slate-300 rounded-md"
                        placeholder="e.g. Review GDPR Compliance"
                        value={newTaskTitle}
                        onChange={e => setNewTaskTitle(e.target.value)}
                    />
                </div>
                <div className="w-full md:w-48">
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Related Tool</label>
                    <select 
                        className="w-full p-2 border border-slate-300 rounded-md bg-white"
                        value={newTaskTool}
                        onChange={e => setNewTaskTool(e.target.value)}
                    >
                        <option value="">-- None --</option>
                        {tools.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                </div>
                <div className="w-full md:w-40">
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Due Date</label>
                    <input 
                        type="date" 
                        className="w-full p-2 border border-slate-300 rounded-md"
                        value={newTaskDate}
                        onChange={e => setNewTaskDate(e.target.value)}
                    />
                </div>
                <button type="submit" className="bg-slate-900 text-white px-6 py-2 rounded-md hover:bg-slate-800">
                    Save
                </button>
            </form>
        </div>
      )}

      {/* Calendrier */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-100 bg-slate-50 flex justify-between items-center flex-wrap gap-2">
          <h3 className="font-bold text-slate-700 flex items-center gap-2">
            <Calendar size={20} className="text-indigo-600" />
            Calendar
          </h3>
          <div className="flex items-center gap-2">
            <button type="button" onClick={prevMonth} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-100 text-slate-600">
              <ChevronLeft size={18} />
            </button>
            <span className="min-w-[180px] text-center font-semibold text-slate-800">{monthLabel}</span>
            <button type="button" onClick={nextMonth} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-100 text-slate-600">
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-7 gap-1 text-center text-xs font-bold text-slate-500 uppercase mb-2">
            {WEEKDAYS.map(d => <div key={d}>{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {daysInGrid.map((day, idx) => {
              const { taskItems, renewalItems } = getItemsForDate(day.dateStr);
              const isToday = day.dateStr === today.toISOString().slice(0, 10);
              return (
                <div
                  key={idx}
                  onClick={() => day.isCurrentMonth && openAddTaskForDate(day.dateStr)}
                  className={`min-h-[72px] p-1.5 rounded-lg border text-left ${
                    !day.isCurrentMonth ? 'bg-slate-50 border-slate-100 text-slate-400' : 'bg-white border-slate-200'
                  } ${isToday ? 'ring-2 ring-indigo-400 ring-offset-1' : ''} ${day.isCurrentMonth ? 'cursor-pointer hover:border-indigo-300 hover:bg-indigo-50/30 transition-colors' : ''}`}
                >
                  <div className="text-xs font-semibold text-slate-600 mb-1">{day.date ?? ''}</div>
                  {day.isCurrentMonth && (
                    <>
                      {taskItems.slice(0, 2).map(t => (
                        <div key={t.id} className="text-[10px] truncate rounded px-1 py-0.5 bg-indigo-100 text-indigo-800 mb-0.5" title={t.title}>
                          {t.title}
                        </div>
                      ))}
                      {taskItems.length > 2 && <div className="text-[10px] text-indigo-600">+{taskItems.length - 2}</div>}
                      {renewalItems.slice(0, 2).map(t => (
                        <div key={t.id} className="text-[10px] truncate rounded px-1 py-0.5 bg-orange-100 text-orange-800" title={t.name}>
                          {t.name}
                        </div>
                      ))}
                      {renewalItems.length > 2 && <div className="text-[10px] text-orange-600">+{renewalItems.length - 2}</div>}
                    </>
                  )}
                </div>
              );
            })}
          </div>
          <div className="flex gap-4 mt-3 text-xs text-slate-500">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-indigo-200" /> Tasks</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-orange-200" /> Renewals</span>
          </div>
        </div>
      </div>

      {/* Liste des Tâches : overdue + 30 jours, puis Show more par 10 */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-100 bg-slate-50 flex justify-between items-center flex-wrap gap-2">
            <h3 className="font-bold text-slate-700 flex items-center gap-2">Compliance & Tasks<InfoTooltip text="Overdue tasks and those due within 30 days. Click 'Show more' to see further." /></h3>
            <span className="text-xs text-slate-500">
              {complianceRelevant.filter(t => !t.isCompleted).length} to process (overdue / next 30 days)
              {complianceRest.length > 0 && ` · ${complianceRest.length} later`}
            </span>
        </div>
        
        {complianceVisible.length === 0 ? (
            <EmptyState
              icon={<CheckCircle size={40} />}
              title="You're all caught up"
              subtitle="No renewals or deadlines in the next 30 days. Tasks linked to tools appear here automatically."
              positive
            />
        ) : (
            <>
            <div className="divide-y divide-slate-100">
                {complianceVisible.map(task => (
                    <div
                        key={task.id}
                        id={`governance-task-${task.id}`}
                        className={`p-4 flex items-center gap-4 hover:bg-slate-50 transition-colors ${task.isCompleted ? 'opacity-50' : ''} ${focusedTaskId === task.id ? 'bg-orange-50 ring-1 ring-orange-300' : ''}`}
                    >
                        <button 
                            onClick={() => handleToggleTask(task.id)}
                            className={`flex-shrink-0 w-6 h-6 rounded border flex items-center justify-center transition-colors
                                ${task.isCompleted 
                                    ? 'bg-green-500 border-green-500 text-white' 
                                    : 'border-slate-300 text-transparent hover:border-indigo-500'}
                            `}
                        >
                            <CheckSquare size={14} />
                        </button>
                        
                        <div className="flex-1">
                            <p className={`font-medium text-slate-800 ${task.isCompleted ? 'line-through' : ''}`}>
                                {task.title}
                            </p>
                            <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                                {task.priority && (
                                    <span data-testid={`priority-badge-${task.priority}`} className={`px-1.5 py-0.5 rounded font-medium ${(PRIORITY_BADGE[task.priority] ?? PRIORITY_BADGE.MEDIUM).bg} ${(PRIORITY_BADGE[task.priority] ?? PRIORITY_BADGE.MEDIUM).text}`}>
                                        {task.priority}
                                    </span>
                                )}
                                {task.dueDate && (
                                    <span className={`flex items-center gap-1 ${task.dueDate < todayStr ? 'text-red-600' : 'text-orange-600'}`}>
                                        <Calendar size={12} /> Due {task.dueDate}
                                        {task.dueDate < todayStr && ' (overdue)'}
                                    </span>
                                )}
                                {task.toolId && (
                                    <span className="bg-slate-100 px-2 py-0.5 rounded text-slate-600">
                                        {tools.find(t => t.id === task.toolId)?.name || 'Unknown Tool'}
                                    </span>
                                )}
                            </div>
                        </div>

                        <button 
                            onClick={() => handleDeleteTask(task.id)}
                            className="text-slate-300 hover:text-red-500 transition-colors"
                        >
                            <Trash2 size={16} />
                        </button>
                    </div>
                ))}
            </div>
            {complianceRest.length > complianceShowMore && (
                <div className="p-3 border-t border-slate-100 bg-slate-50 text-center">
                    <button
                        type="button"
                        onClick={() => setComplianceShowMore(prev => prev + 10)}
                        className="text-sm font-medium text-indigo-600 hover:text-indigo-700"
                    >
                        Show more (+10) — {complianceRest.length - complianceShowMore} remaining
                    </button>
                </div>
            )}
            </>
        )}
      </div>

      {/* Modal après complétion : rappel (renewal/notice) ou créer une autre */}
      {taskJustCompleted && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full overflow-hidden">
            <div className="p-4 border-b border-slate-100 flex justify-between items-center">
              <h3 className="font-bold text-slate-800">Task completed</h3>
              <button type="button" onClick={() => { setTaskJustCompleted(null); setNextReminderDate(''); }} className="text-slate-400 hover:text-slate-600 p-1">
                <X size={20} />
              </button>
            </div>
            <div className="p-6">
              {isToolDateTask(taskJustCompleted) ? (
                <form onSubmit={handleSetNextReminder}>
                  <p className="text-sm text-slate-600 mb-4">
                    Set the next reminder for <strong>{tools.find(t => t.id === taskJustCompleted.toolId)?.name || 'this tool'}</strong>.
                  </p>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Next reminder date</label>
                    <input
                      type="date"
                      required
                      value={nextReminderDate}
                      onChange={e => setNextReminderDate(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <button type="button" onClick={() => { setTaskJustCompleted(null); setNextReminderDate(''); }} className="px-4 py-2 border border-green-300 text-green-700 rounded-lg hover:bg-green-50 font-medium">
                      Done
                    </button>
                    <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
                      <span className="hidden sm:inline">Update and create reminder</span>
                      <span className="sm:hidden">Update reminder</span>
                    </button>
                  </div>
                </form>
              ) : isReviewTask(taskJustCompleted) ? (
                <>
                  <p className="text-sm text-slate-600 mb-4">
                    Review completed for <strong>{tools.find(t => t.id === taskJustCompleted.toolId)?.name || 'this tool'}</strong>.
                    {nextReminderDate && <> Next review scheduled for <strong>{nextReminderDate}</strong>.</>}
                  </p>
                  <p className="text-xs text-slate-500 mb-4">A new review task will be created automatically on next sync.</p>
                  <div className="flex justify-end gap-2">
                    <button type="button" onClick={() => { setTaskJustCompleted(null); setNextReminderDate(''); onReloadTasks(); }} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
                      OK
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm text-slate-600 mb-4">Would you like to create another task?</p>
                  <div className="flex justify-end gap-2">
                    <button type="button" onClick={() => setTaskJustCompleted(null)} className="px-4 py-2 border border-green-300 text-green-700 rounded-lg hover:bg-green-50 font-medium">
                      Done
                    </button>
                    <button type="button" onClick={openAddTaskAndClose} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
                      Create another task
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};